import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { getApiBaseUrl } from './langgananUtil';
import { notif, konfirmasi } from '../../utils/dialog';

const fetcher = (url) => axios.get(url).then((r) => r.data.data);
const fetcherKatalog = (url) => axios.get(url).then((r) => ({ fitur: r.data.data, inti: r.data.inti || [] }));

// Matriks fitur x paket. Paket "semua fitur" otomatis mencakup semua fitur (termasuk yang baru).
const PaketFitur = () => {
  const base = getApiBaseUrl();
  const { data: katalogData } = useSWR(`${base}/fitur-katalog`, fetcherKatalog);
  const katalog = katalogData?.fitur;
  const inti = katalogData?.inti || [];
  const { data: pakets, mutate } = useSWR(`${base}/paket`, fetcher);

  // Perubahan lokal yang belum disimpan: { [paketUuid]: { nama, deskripsi, semuaFitur, fitur:Set } }
  const [draft, setDraft] = useState({});
  const [notif, setNotif] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: 'baru' | 'edit', uuid?, nama, deskripsi }

  const tampil = useMemo(() => {
    if (!pakets) return [];
    return pakets.map((p) => {
      const d = draft[p.uuid];
      return d || { uuid: p.uuid, nama: p.nama, deskripsi: p.deskripsi, semuaFitur: p.semuaFitur, fitur: new Set(p.fitur), jumlahCabang: p.jumlahCabang, dirty: false };
    }).map((p) => ({ ...p, jumlahCabang: pakets.find((x) => x.uuid === p.uuid)?.jumlahCabang ?? 0 }));
  }, [pakets, draft]);

  const grup = useMemo(() => {
    const m = new Map();
    (katalog || []).forEach((f) => {
      if (!m.has(f.group)) m.set(f.group, []);
      m.get(f.group).push(f);
    });
    return [...m.entries()];
  }, [katalog]);

  const ubah = (p, perubahan) => {
    setDraft((prev) => ({ ...prev, [p.uuid]: { ...p, ...perubahan, dirty: true } }));
  };

  const toggleFitur = (p, key) => {
    const set = new Set(p.fitur);
    if (set.has(key)) set.delete(key); else set.add(key);
    ubah(p, { fitur: set });
  };

  const adaPerubahan = Object.values(draft).some((d) => d.dirty);

  const simpanSemua = async () => {
    setSaving(true);
    try {
      for (const d of Object.values(draft).filter((x) => x.dirty)) {
        await axios.put(`${base}/paket/${d.uuid}`, {
          nama: d.nama,
          deskripsi: d.deskripsi,
          semuaFitur: d.semuaFitur,
          fitur: [...d.fitur],
        });
      }
      setDraft({});
      await mutate();
      setNotif({ severity: 'success', text: 'Perubahan paket disimpan. Berlaku untuk semua cabang berpaket tersebut.' });
    } catch (e) {
      setNotif({ severity: 'error', text: e.response?.data?.message || 'Gagal menyimpan' });
    } finally {
      setSaving(false);
    }
  };

  const simpanDialog = async () => {
    if (!dialog.nama.trim()) return;
    try {
      if (dialog.mode === 'baru') {
        await axios.post(`${base}/paket`, { nama: dialog.nama, deskripsi: dialog.deskripsi, semuaFitur: false, fitur: [] });
        setNotif({ severity: 'success', text: 'Paket dibuat. Centang fitur yang termasuk, lalu Simpan.' });
      } else {
        const p = tampil.find((x) => x.uuid === dialog.uuid);
        ubah(p, { nama: dialog.nama, deskripsi: dialog.deskripsi });
      }
      setDialog(null);
      await mutate();
    } catch (e) {
      setNotif({ severity: 'error', text: e.response?.data?.message || 'Gagal menyimpan paket' });
    }
  };

  const hapus = async (p) => {
    if (!await konfirmasi(`Hapus paket "${p.nama}"?`)) return;
    try {
      await axios.delete(`${base}/paket/${p.uuid}`);
      setDraft((prev) => { const n = { ...prev }; delete n[p.uuid]; return n; });
      await mutate();
      setNotif({ severity: 'success', text: 'Paket dihapus' });
    } catch (e) {
      setNotif({ severity: 'error', text: e.response?.data?.message || 'Gagal menghapus paket' });
    }
  };

  if (!katalog || !pakets) return <Typography sx={{ p: 2 }}>Memuat...</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="h6">Paket & Fitur</Typography>
          <Typography variant="body2" color="text.secondary">
            Centang fitur yang termasuk di tiap paket. Perubahan berlaku ke semua cabang yang memakai paket itu.
            Fitur inti (login, produk dasar, user, konfigurasi, dashboard) selalu aktif.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialog({ mode: 'baru', nama: '', deskripsi: '' })}>Tambah Paket</Button>
          <Button variant="contained" disabled={!adaPerubahan || saving} onClick={simpanSemua}>Simpan Perubahan</Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 260 }}>Fitur</TableCell>
              {tampil.map((p) => (
                <TableCell key={p.uuid} align="center" sx={{ minWidth: 130, verticalAlign: 'top' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography fontWeight={700}>{p.nama}</Typography>
                    <Tooltip title="Ubah nama/deskripsi">
                      <IconButton size="small" onClick={() => setDialog({ mode: 'edit', uuid: p.uuid, nama: p.nama, deskripsi: p.deskripsi || '' })}>
                        <EditOutlined fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={p.jumlahCabang > 0 ? `Dipakai ${p.jumlahCabang} cabang` : 'Hapus paket'}>
                      <span>
                        <IconButton size="small" disabled={p.jumlahCabang > 0} onClick={() => hapus(p)}>
                          <DeleteOutline fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Chip size="small" label={`${p.jumlahCabang} cabang`} sx={{ mt: 0.5 }} />
                  <Box>
                    <Tooltip title="Semua fitur otomatis termasuk, juga fitur yang ditambahkan nanti">
                      <FormLabelSwitch checked={p.semuaFitur} onChange={(v) => ubah(p, { semuaFitur: v })} />
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {inti.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={tampil.length + 1} sx={{ bgcolor: 'action.hover', fontWeight: 700 }}>
                    Fitur Inti <Typography component="span" variant="caption" color="text.secondary">(selalu aktif di semua paket)</Typography>
                  </TableCell>
                </TableRow>
                {inti.map((f) => (
                  <TableRow key={f.key} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{f.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.deskripsi}</Typography>
                    </TableCell>
                    {tampil.map((p) => (
                      <TableCell key={p.uuid} align="center">
                        <Checkbox checked disabled />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}
            {grup.map(([nama, fiturs]) => (
              <React.Fragment key={nama}>
                <TableRow>
                  <TableCell colSpan={tampil.length + 1} sx={{ bgcolor: 'action.hover', fontWeight: 700 }}>{nama}</TableCell>
                </TableRow>
                {fiturs.map((f) => (
                  <TableRow key={f.key} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{f.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.deskripsi}</Typography>
                    </TableCell>
                    {tampil.map((p) => (
                      <TableCell key={p.uuid} align="center">
                        <Checkbox
                          checked={p.semuaFitur || p.fitur.has(f.key)}
                          disabled={p.semuaFitur}
                          onChange={() => toggleFitur(p, f.key)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{dialog?.mode === 'baru' ? 'Tambah Paket' : 'Ubah Paket'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Nama paket" value={dialog?.nama || ''} onChange={(e) => setDialog({ ...dialog, nama: e.target.value })} />
          <TextField fullWidth margin="dense" label="Deskripsi (opsional)" multiline minRows={2} value={dialog?.deskripsi || ''} onChange={(e) => setDialog({ ...dialog, deskripsi: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Batal</Button>
          <Button variant="contained" onClick={simpanDialog}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(notif)} autoHideDuration={5000} onClose={() => setNotif(null)}>
        {notif ? <Alert severity={notif.severity} onClose={() => setNotif(null)}>{notif.text}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

const FormLabelSwitch = React.forwardRef(({ checked, onChange, ...rest }, ref) => (
  <Box ref={ref} {...rest} sx={{ display: 'inline-flex', alignItems: 'center' }}>
    <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <Typography variant="caption">Semua fitur</Typography>
  </Box>
));

export default PaketFitur;
